import { z } from 'zod'

import {
  publicFormProjectionSchema,
  publicFormSubmitInputSchema,
} from '../site-forms/form-handoff-schemas'
import {
  PendingItemsChannel,
  eSignConsentProfileSchema,
  pendingItemHandoffCapabilitySchema,
  pendingItemKindSchema,
  pendingItemsChannelSchema,
  pendingItemsHandoffStatusSchema,
  policyAcceptanceMethodSchema,
  readinessReasonCodeSchema,
  signatureModeSchema,
  signerCapacitySchema,
} from './policy-compliance-enums'

export const PENDING_ITEMS_STAFF_CHANNELS = [
  PendingItemsChannel.SMS,
  PendingItemsChannel.QR,
  PendingItemsChannel.DESK,
  PendingItemsChannel.KIOSK,
  PendingItemsChannel.APP,
] as const

export const submissionIdSchema = z
  .string()
  .min(8)
  .max(64)
  .regex(/^[A-Za-z0-9_-]+$/)

const e164Schema = z.string().regex(/^\+[1-9]\d{6,14}$/)

export const createPendingItemsHandoffInputSchema = z
  .object({
    siteId: z.uuid(),
    clientId: z.uuid(),
    recipientCapacity: signerCapacitySchema,
    guardianClientId: z.uuid().nullish(),
    channel: pendingItemsChannelSchema,
    mobileOverride: e164Schema.nullish(),
    smsConsentAttested: z.boolean().nullish(),
    deviceTokenId: z.uuid().nullish(),
    guardianIdChecked: z.boolean().nullish(),
    idempotencyKey: z.string().min(1).max(128),
  })
  .superRefine((input, ctx) => {
    if (!(PENDING_ITEMS_STAFF_CHANNELS as readonly string[]).includes(input.channel)) {
      ctx.addIssue({ code: 'custom', message: 'Staff cannot send this channel', path: ['channel'] })
    }
    if ((input.recipientCapacity === 'GUARDIAN') !== !!input.guardianClientId) {
      ctx.addIssue({
        code: 'custom',
        message: 'A guardian recipient names the guardian, and only a guardian recipient does',
        path: ['guardianClientId'],
      })
    }
    if (
      input.guardianIdChecked &&
      (input.channel !== PendingItemsChannel.DESK || input.recipientCapacity !== 'GUARDIAN')
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'A guardian photo ID check is recorded only for a guardian signing at the desk',
        path: ['guardianIdChecked'],
      })
    }
    if ((input.channel === PendingItemsChannel.KIOSK) !== !!input.deviceTokenId) {
      ctx.addIssue({
        code: 'custom',
        message: 'A kiosk is required for the kiosk channel and only for it',
        path: ['deviceTokenId'],
      })
    }
  })

export const cancelPendingItemsHandoffInputSchema = z.object({
  siteId: z.uuid(),
  handoffId: z.uuid(),
})

export const PENDING_ITEMS_PRESENCE_BINDING_LENGTH = 43

export const pendingItemsTokenExchangeInputSchema = z.object({
  token: z.string().min(16).max(64),
  binding: z.string().length(PENDING_ITEMS_PRESENCE_BINDING_LENGTH).nullish(),
})

export const pendingItemsVerifyInputSchema = z.object({
  submissionId: submissionIdSchema,
  dateOfBirth: z.iso.date(),
})

export const PENDING_ITEMS_ACCESS_CODE_LENGTH = 6

export const pendingItemsConsentInputSchema = z.object({
  submissionId: submissionIdSchema,
  disclosureRevisionId: z.uuid(),
  agreed: z.literal(true),
  copiesEmail: z.email().max(320).nullish(),
  accessCode: z
    .string()
    .trim()
    .toUpperCase()
    .length(PENDING_ITEMS_ACCESS_CODE_LENGTH)
    .regex(/^[A-Z0-9]+$/)
    .nullish(),
})

export const GuardianDeclarationRelationship = {
  PARENT: 'PARENT',
  LEGAL_GUARDIAN: 'LEGAL_GUARDIAN',
} as const

export const pendingItemsGuardianDeclarationSchema = z.object({
  fullName: z.string().trim().min(1).max(200),
  relationship: z.enum(GuardianDeclarationRelationship),
  authorityDeclared: z.literal(true),
})

export const pendingItemsPolicySubmitInputSchema = z.object({
  submissionId: submissionIdSchema,
  itemKey: z.string().min(1).max(200),
  policyRevisionId: z.uuid(),
  revisionSha256: z.string().regex(/^[0-9a-f]{64}$/),
  typedName: z.string().trim().min(1).max(200),
  accepted: z.boolean().nullish(),
  signature: z
    .object({
      mode: signatureModeSchema,
      uploadId: z.uuid().nullish(),
    })
    .nullish(),
  guardianDeclaration: pendingItemsGuardianDeclarationSchema.nullish(),
})

export const pendingItemsSignatureFinalizeInputSchema = z.object({
  uploadId: z.uuid(),
})

export const pendingItemsSkinTypeSubmitInputSchema = z.object({
  submissionId: submissionIdSchema,
  itemKey: z.string().min(1).max(200),
  answers: publicFormSubmitInputSchema.shape.answers,
})

export const pendingItemsLinkedItemOpenInputSchema = z.object({
  itemKey: z.string().min(1).max(200),
})

export const pendingItemsLinkedItemTargetSchema = z.object({
  itemKey: z.string(),
  capability: pendingItemHandoffCapabilitySchema,
  url: z.string(),
  expiresAt: z.date().nullable(),
})

export const pendingItemsKioskClaimInputSchema = z.object({
  handoffId: z.uuid(),
  leaseVersion: z.number().int().nonnegative(),
})

export const pendingItemsKioskCurrentSchema = z.object({
  handoffId: z.uuid(),
  leaseVersion: z.number().int().nonnegative(),
  recipientCapacity: signerCapacitySchema,
  expiresAt: z.date(),
})

export const pendingItemsKioskClaimSchema = z.object({
  handoffId: z.uuid(),
  leaseVersion: z.number().int().nonnegative(),
  token: z.string(),
  binding: z.string(),
  url: z.string(),
  expiresAt: z.date(),
})

export const pendingItemsPageItemSchema = z.object({
  itemKey: z.string(),
  kind: pendingItemKindSchema,
  title: z.string(),
  reasonCode: readinessReasonCodeSchema,
  sitePolicyId: z.uuid().nullish(),
  policyRevisionId: z.uuid().nullish(),
  revisionSha256: z.string().nullish(),
  acceptanceMethod: policyAcceptanceMethodSchema.nullish(),
  content: z.string().nullish(),
  signerCapacity: signerCapacitySchema.nullish(),
  requiredForEquipment: z.boolean().nullish(),
  dueOn: z.iso.date().nullish(),
  validUntil: z.iso.date().nullish(),
  capability: pendingItemHandoffCapabilitySchema,
  form: publicFormProjectionSchema.nullish(),
})

export const pendingItemsLinkedPageItemSchema = z.object({
  itemKey: z.string(),
  kind: pendingItemKindSchema,
  title: z.string(),
  capability: pendingItemHandoffCapabilitySchema,
  done: z.boolean(),
})

export const PendingItemsStepStatus = {
  REQUIRED: 'REQUIRED',
  DONE: 'DONE',
  NOT_REQUIRED: 'NOT_REQUIRED',
} as const
export type PendingItemsStepStatus =
  (typeof PendingItemsStepStatus)[keyof typeof PendingItemsStepStatus]

const stepStatusSchema = z.enum(PendingItemsStepStatus)

export const PendingItemsDateOrder = {
  DAY_FIRST: 'DAY_FIRST',
  MONTH_FIRST: 'MONTH_FIRST',
} as const
export type PendingItemsDateOrder =
  (typeof PendingItemsDateOrder)[keyof typeof PendingItemsDateOrder]

export const pendingItemsPublicLocationSchema = z.object({
  name: z.string().nullable(),
  timezone: z.string().nullable(),
  dateFormat: z.enum(PendingItemsDateOrder).nullable(),
  locale: z.string().nullable(),
})

export const pendingItemsPageModelSchema = z.object({
  handoffId: z.uuid(),
  status: pendingItemsHandoffStatusSchema,
  channel: pendingItemsChannelSchema,
  expiresAt: z.date(),
  recipient: z.object({ firstName: z.string().nullable(), capacity: signerCapacitySchema }),
  participant: z.object({ firstName: z.string().nullable() }),
  location: pendingItemsPublicLocationSchema,
  steps: z.object({
    identity: stepStatusSchema,
    guardianDeclaration: stepStatusSchema,
    electronicConsent: stepStatusSchema,
  }),
  identityAttemptsRemaining: z.number().int().nonnegative().nullable(),
  disclosureRevisionId: z.uuid(),
  eSignConsentProfile: eSignConsentProfileSchema,
  accessCodeRequired: z.boolean(),
  accessCodeIssued: z.boolean(),
  blockingItems: z.array(pendingItemsPageItemSchema),
  actionableItems: z.array(pendingItemsPageItemSchema),
  upcomingItems: z.array(pendingItemsPageItemSchema),
  linkedItems: z.array(pendingItemsLinkedPageItemSchema),
  doneItems: z.array(z.object({ itemKey: z.string(), title: z.string() })),
  awaitingGuardian: z.boolean().nullish(),
  doneCount: z.number().int().nonnegative(),
  totalCount: z.number().int().nonnegative(),
  receiptUrl: z.string().nullable(),
  copiesEmailed: z.boolean().nullish(),
})

export const pendingItemsSubmitResultSchema = z.object({
  itemKey: z.string().nullable(),
  outcome: z.enum(['RECORDED', 'ALREADY_SATISFIED', 'DUPLICATE', 'VERIFIED', 'CONSENTED']),
  agreementId: z.uuid().nullable(),
  status: pendingItemsHandoffStatusSchema,
  doneCount: z.number().int().nonnegative(),
  totalCount: z.number().int().nonnegative(),
  receiptId: z.uuid().nullable(),
  receiptUrl: z.string().nullable(),
})

export const pendingItemsReceiptDocumentSchema = z.object({
  agreementId: z.uuid(),
  title: z.string(),
  signedAt: z.date().nullable(),
  signerCapacity: signerCapacitySchema.nullable(),
  evidenceStatus: z.string().nullable(),
  copyStatus: z.string().nullable(),
})

export const pendingItemsReceiptModelSchema = z.object({
  receiptId: z.uuid(),
  expiresAt: z.date().nullable(),
  recipient: z.object({ firstName: z.string().nullable(), capacity: signerCapacitySchema }),
  location: pendingItemsPublicLocationSchema,
  completedAt: z.date().nullable(),
  documents: z.array(pendingItemsReceiptDocumentSchema),
})

export type PendingItemsPublicLocation = z.infer<typeof pendingItemsPublicLocationSchema>
export type CreatePendingItemsHandoffInput = z.infer<typeof createPendingItemsHandoffInputSchema>
export type CancelPendingItemsHandoffInput = z.infer<typeof cancelPendingItemsHandoffInputSchema>
export type PendingItemsVerifyInput = z.infer<typeof pendingItemsVerifyInputSchema>
export type PendingItemsConsentInput = z.infer<typeof pendingItemsConsentInputSchema>
export type PendingItemsGuardianDeclaration = z.infer<typeof pendingItemsGuardianDeclarationSchema>
export type PendingItemsPolicySubmitInput = z.infer<typeof pendingItemsPolicySubmitInputSchema>
export type PendingItemsPageItem = z.infer<typeof pendingItemsPageItemSchema>
export type PendingItemsPageModel = z.infer<typeof pendingItemsPageModelSchema>
export type PendingItemsSubmitResult = z.infer<typeof pendingItemsSubmitResultSchema>
export type PendingItemsReceiptDocument = z.infer<typeof pendingItemsReceiptDocumentSchema>
export type PendingItemsReceiptModel = z.infer<typeof pendingItemsReceiptModelSchema>
export type PendingItemsSkinTypeSubmitInput = z.infer<typeof pendingItemsSkinTypeSubmitInputSchema>
export type PendingItemsLinkedItemTarget = z.infer<typeof pendingItemsLinkedItemTargetSchema>
export type PendingItemsLinkedPageItem = z.infer<typeof pendingItemsLinkedPageItemSchema>
export type PendingItemsKioskClaimInput = z.infer<typeof pendingItemsKioskClaimInputSchema>
export type PendingItemsKioskCurrent = z.infer<typeof pendingItemsKioskCurrentSchema>
export type PendingItemsKioskClaim = z.infer<typeof pendingItemsKioskClaimSchema>
